/**
 * ReviewForm.jsx
 *
 * Component for submitting game reviews
 * - Star rating selection (1-5 stars)
 * - Text comment (10-500 characters)
 * - Form validation and error handling
 * - Submit to /api/reviews/submit endpoint
 *
 * Props:
 * - playerName: string - Name of the player submitting review
 * - gameId: string - ID of the game being reviewed
 * - onSubmit: function - Callback when review submitted successfully
 * - onCancel: function - Callback to close the form
 */

import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { sanitizeInput } from '../utils/sanitizer';

const ReviewForm = ({ playerName, gameId, onSubmit, onCancel }) => {
  const { t } = useLanguage();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (rating === 0) {
      setError(t('reviews.errorNoRating'));
      return;
    }

    const trimmedComment = comment.trim();

    if (trimmedComment.length < 10) {
      setError(t('reviews.errorTooShort'));
      return;
    }

    if (trimmedComment.length > 500) {
      setError(t('reviews.errorTooLong'));
      return;
    }

    // Check minimum word count
    const wordCount = trimmedComment.split(/\s+/).length;
    if (wordCount < 3) {
      setError(t('reviews.errorMinWords'));
      return;
    }

    setSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001';
      const response = await fetch(`${apiUrl}/api/reviews/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerName: sanitizeInput(playerName),
          rating: rating,
          comment: sanitizeInput(trimmedComment),
          gameId: sanitizeInput(gameId),
          timestamp: Date.now(),
          userAgent: navigator.userAgent,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Store timestamp to prevent showing review prompt for one week
        localStorage.setItem('qwixx_review_submitted_time', Date.now().toString());
        onSubmit();
      } else {
        setError(data.error || t('reviews.errorGeneric'));
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(t('reviews.errorNetwork'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle star rating click
   */
  const handleStarClick = (star) => {
    setRating(star);
    setError(''); // Clear error when user makes selection
  };

  /**
   * Handle comment text change
   */
  const handleCommentChange = (e) => {
    const text = e.target.value;
    if (text.length <= 500) {
      setComment(text);
      setError(''); // Clear error when user types
    }
  };

  /**
   * Render star rating UI
   */
  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= (hoverRating || rating);
      stars.push(
        <span
          key={i}
          className={`review-star ${isFilled ? 'filled' : ''}`}
          onClick={() => handleStarClick(i)}
          onMouseEnter={() => setHoverRating(i)}
          onMouseLeave={() => setHoverRating(0)}
          role="button"
          tabIndex={0}
          aria-label={`${i} ${i === 1 ? t('reviews.star') : t('reviews.stars')}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleStarClick(i);
            }
          }}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="review-form-overlay">
      <form className="review-form" onSubmit={handleSubmit}>
        <h3 className="review-form-title">{t('reviews.title')}</h3>

        {/* Informational message about where review will appear */}
        <div className="review-info-message">
          <p>{t('reviews.visibilityInfo')}</p>
        </div>

        {/* Star Rating */}
        <div className="review-form-section">
          <label className="review-form-label">{t('reviews.ratingLabel')}</label>
          <div className="review-star-rating" role="radiogroup" aria-label={t('reviews.ratingLabel')}>
            {renderStars()}
          </div>
          {rating > 0 && (
            <p className="review-rating-text">
              {rating} {rating === 1 ? t('reviews.star') : t('reviews.stars')}
            </p>
          )}
        </div>

        {/* Comment Textarea */}
        <div className="review-form-section">
          <label className="review-form-label" htmlFor="review-comment">
            {t('reviews.commentLabel')}
          </label>
          <textarea
            id="review-comment"
            className="review-form-textarea"
            placeholder={t('reviews.placeholder')}
            value={comment}
            onChange={handleCommentChange}
            maxLength={500}
            rows={5}
            disabled={submitting}
            aria-describedby="character-count"
          />
          <div className="review-character-count" id="character-count">
            {comment.length}/500 {t('reviews.characters')}
            {comment.length < 10 && comment.length > 0 && (
              <span className="min-length-warning">
                {' '}
                ({t('reviews.minCharWarning')})
              </span>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="review-error-message" role="alert">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="review-form-actions">
          <button
            type="submit"
            className="review-submit-btn"
            disabled={submitting || rating === 0}
            aria-busy={submitting}
          >
            {submitting ? t('reviews.submitting') : t('reviews.submit')}
          </button>
          <button
            type="button"
            className="review-cancel-btn"
            onClick={onCancel}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
        </div>

        {/* Privacy Note */}
        <p className="review-privacy-note">{t('reviews.privacyNote')}</p>
      </form>
    </div>
  );
};

export default ReviewForm;
